import {ContentCopy, Delete, Image} from '@mui/icons-material';
import {Box, Button, Chip, IconButton, Paper, Tooltip} from '@mui/material';
import MDTypography from 'components/MDTypography';
import {useEffect, useState} from 'react';
import DeviceTabs from './DeviceTabs';
import Dropzone from 'react-dropzone';

const DeviceImageUploader = ({
  value = {},
  onChange,
  label,
  files = {},
  onFileChange,
  error,
  helperText,
  required = false, 
  deviceTypes = ['mobile', 'tablet', 'desktop'], 
  showCopyToAll = true,
}) => {
  const [activeDevice, setActiveDevice] = useState(deviceTypes[2] || 'desktop'); 
  const [previews, setPreviews] = useState({});
  const [localFiles, setLocalFiles] = useState({});

  useEffect(() => {
    const initialPreviews = {};
    deviceTypes.forEach(device => {
      if (value && value[device]) {
        initialPreviews[device] = value[device];
      }
    });
    setPreviews(initialPreviews);
  }, [value, deviceTypes]);

  useEffect(() => {
    const initialFiles = {};
    deviceTypes.forEach(device => {
      if (files && files[device]) {
        initialFiles[device] = files[device];
      }
    });
    setLocalFiles(initialFiles);
  }, [files, deviceTypes]);

  const handleDrop = (acceptedFiles, device) => {
    const newFile = acceptedFiles[0];
    if (!newFile) return;

    const objectUrl = URL.createObjectURL(newFile);

    setPreviews(prev => ({
      ...prev,
      [device]: objectUrl,
    }));

    setLocalFiles(prev => ({
      ...prev,
      [device]: newFile,
    }));

    const updatedValue = {
      ...value,
      [device]: objectUrl,
    };
    
    deviceTypes.forEach(d => {
      if (!updatedValue[d] && value && value[d]) {
        updatedValue[d] = value[d];
      }
    });
    
    onChange(updatedValue);

    if (onFileChange) {
      onFileChange(device, newFile);
    }
  };

  const clearImage = device => {
    if (previews[device]?.startsWith('blob:')) {
      URL.revokeObjectURL(previews[device]);
    }

    setPreviews(prev => ({
      ...prev,
      [device]: '',
    }));

    setLocalFiles(prev => ({
      ...prev,
      [device]: null,
    }));

    const updatedValue = {...value};
    delete updatedValue[device];
    
    onChange(updatedValue);

    if (onFileChange) {
      onFileChange(device, null);
    }
  };

  const copyFromDevice = sourceDevice => {
    if (!previews[sourceDevice]) return;

    setPreviews(prev => ({
      ...prev,
      [activeDevice]: prev[sourceDevice],
    }));

    setLocalFiles(prev => ({
      ...prev,
      [activeDevice]: prev[sourceDevice],
    }));

    const updatedValue = {
      ...value,
      [activeDevice]: value[sourceDevice],
    };
    onChange(updatedValue);
  };

  const copyAllToDevices = () => {
    if (!previews.desktop) return;

    const newPreviews = {};
    const newLocalFiles = {};
    const newValue = {};

    deviceTypes.forEach(device => {
      newPreviews[device] = previews.desktop;
      newLocalFiles[device] = localFiles.desktop;
      newValue[device] = value.desktop;
    });

    setPreviews(newPreviews);
    setLocalFiles(newLocalFiles);
    onChange(newValue);
  };

  const hasAnyImage = Object.values(previews).some(url => url);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}>
        <MDTypography variant="caption" color={error ? 'error' : 'text'}>
          {label} {required && <span style={{color: 'red'}}>*</span>} 
        </MDTypography>
        {/* {showCopyToAll && previews.desktop && (
          <Tooltip title="Use same image for all devices">
            <Button
              size="small"
              startIcon={<ContentCopy />}
              onClick={copyAllToDevices}>
              Copy to All
            </Button>
          </Tooltip>
        )} */}
      </Box>

      <DeviceTabs
        activeDevice={activeDevice}
        onDeviceChange={setActiveDevice}
        deviceTypes={deviceTypes} 
      />

      <Box sx={{position: 'relative'}}>
        <Dropzone
          onDrop={files => handleDrop(files, activeDevice)}
          accept={{'image/*': []}}
          maxFiles={1}>
          {({getRootProps, getInputProps}) => (
            <Paper
              {...getRootProps()}
              sx={{
                p: 2,
                border: '2px dashed',
                borderColor: error ? 'error.main' : 'grey.300',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {borderColor: 'primary.main'},
                bgcolor: error ? 'error.light' : 'transparent',
              }}>
              <input {...getInputProps()} />

              {previews[activeDevice] ? (
                <Box>
                  <Box sx={{position: 'relative', display: 'inline-block'}}>
                    <img
                      src={previews[activeDevice]}
                      alt={`${activeDevice} preview`}
                      style={{
                        maxHeight: 150,
                        maxWidth: '100%',
                        borderRadius: 4,
                      }}
                    />
                  </Box>

                  <MDTypography variant="caption" display="block" sx={{mt: 1}}>
                    Click or drag to change {activeDevice} image
                  </MDTypography>

                  {localFiles[activeDevice] && (
                    <MDTypography
                      variant="caption"
                      color="success"
                      display="block">
                      New: {localFiles[activeDevice].name}
                    </MDTypography>
                  )}
                </Box>
              ) : (
                <Box>
                  <Image sx={{fontSize: 40, color: 'text.secondary'}} />
                  <MDTypography variant="body2" color="text">
                    Upload {activeDevice} image
                  </MDTypography>
                </Box>
              )}
            </Paper>
          )}
        </Dropzone>

        {previews[activeDevice] && (
          <IconButton
            size="small"
            color="error"
            onClick={() => clearImage(activeDevice)}
            sx={{
              position: 'absolute',
              top: 5,
              right: 5,
              bgcolor: 'white',
              '&:hover': {bgcolor: '#f5f5f5'},
            }}>
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box display="flex" gap={1} mt={1}>
        {deviceTypes.map(device => (
          <Tooltip
            key={device}
            title={previews[device] ? `${device} image set` : `No ${device} image`}>
            <Chip
              label={device}
              size="small"
              color={previews[device] ? 'success' : 'default'}
              variant={previews[device] ? 'filled' : 'outlined'}
              onClick={() => setActiveDevice(device)}
              sx={{cursor: 'pointer', textTransform: 'capitalize'}}
            />
          </Tooltip>
        ))}
      </Box>

      {error && (
        <MDTypography
          variant="caption"
          color="error"
          sx={{mt: 0.5, display: 'block'}}>
          {helperText}
        </MDTypography>
      )}
    </Box>
  );
};

export default DeviceImageUploader;

// OLD WITH all device specific...
// import {ContentCopy, Delete, Image} from '@mui/icons-material';
// import {Box, Button, Chip, IconButton, Paper, Tooltip} from '@mui/material';
// import MDTypography from 'components/MDTypography';
// import {useEffect, useState} from 'react';
// import DeviceTabs from './DeviceTabs';
// import Dropzone from 'react-dropzone';

// const DeviceImageUploader = ({
//   value = {},
//   onChange,
//   label,
//   files = {},
//   onFileChange,
//   error,
//   helperText,
// }) => {
//   const [activeDevice, setActiveDevice] = useState('desktop');
//   const [previews, setPreviews] = useState({
//     mobile: value?.mobile || '',
//     tablet: value?.tablet || '',
//     desktop: value?.desktop || '',
//   });
//   const [localFiles, setLocalFiles] = useState({
//     mobile: files?.mobile || null,
//     tablet: files?.tablet || null,
//     desktop: files?.desktop || null,
//   });

//   useEffect(() => {
//     setPreviews({
//       mobile: value?.mobile || '',
//       tablet: value?.tablet || '',
//       desktop: value?.desktop || '',
//     });
//   }, [value]);

//   useEffect(() => {
//     setLocalFiles({
//       mobile: files?.mobile || null,
//       tablet: files?.tablet || null,
//       desktop: files?.desktop || null,
//     });
//   }, [files]);

//   const handleDrop = (acceptedFiles, device) => {
//     const newFile = acceptedFiles[0];
//     if (!newFile) return;

//     const objectUrl = URL.createObjectURL(newFile);

//     setPreviews(prev => ({
//       ...prev,
//       [device]: objectUrl,
//     }));

//     const updatedFiles = {
//       ...localFiles,
//       [device]: newFile,
//     };
//     setLocalFiles(updatedFiles);

//     const updatedValue = {
//       ...value,
//       [device]: objectUrl,
//     };
//     onChange(updatedValue);

//     if (onFileChange) {
//       onFileChange(device, newFile);
//     }
//   };

//   const clearImage = device => {
//     if (previews[device]?.startsWith('blob:')) {
//       URL.revokeObjectURL(previews[device]);
//     }

//     setPreviews(prev => ({
//       ...prev,
//       [device]: '',
//     }));

//     setLocalFiles(prev => ({
//       ...prev,
//       [device]: null,
//     }));

//     const updatedValue = {...value};
//     delete updatedValue[device];

//     if (Object.keys(updatedValue).length === 0) {
//       onChange({});
//     } else {
//       onChange(updatedValue);
//     }

//     if (onFileChange) {
//       onFileChange(device, null);
//     }
//   };

//   const copyFromDevice = sourceDevice => {
//     if (!previews[sourceDevice]) return;

//     setPreviews(prev => ({
//       ...prev,
//       [activeDevice]: prev[sourceDevice],
//     }));

//     setLocalFiles(prev => ({
//       ...prev,
//       [activeDevice]: prev[sourceDevice],
//     }));

//     const updatedValue = {
//       ...value,
//       [activeDevice]: value[sourceDevice],
//     };
//     onChange(updatedValue);
//   };

//   const copyAllToDevices = () => {
//     if (!previews.desktop) return;

//     setPreviews({
//       mobile: previews.desktop,
//       tablet: previews.desktop,
//       desktop: previews.desktop,
//     });

//     setLocalFiles({
//       mobile: localFiles.desktop,
//       tablet: localFiles.desktop,
//       desktop: localFiles.desktop,
//     });

//     const updatedValue = {
//       mobile: value.desktop,
//       tablet: value.desktop,
//       desktop: value.desktop,
//     };
//     onChange(updatedValue);
//   };

//   const hasAnyImage = Object.values(previews).some(url => url);

//   return (
//     <Box>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={1}>
//         <MDTypography variant="caption" color={error ? 'error' : 'text'}>
//           {label} {hasAnyImage ? '' : ''}
//         </MDTypography>
//         {/* {previews.desktop && (
//           <Box>
//             <Tooltip title="Use same for all devices">
//               <Button
//                 size="small"
//                 startIcon={<ContentCopy />}
//                 onClick={copyAllToDevices}
//                 sx={{mr: 1}}>
//                 Copy to All
//               </Button>
//             </Tooltip>
//           </Box>
//         )} */}
//       </Box>

//       <DeviceTabs
//         activeDevice={activeDevice}
//         onDeviceChange={setActiveDevice}
//       />

//       <Box sx={{position: 'relative'}}>
//         <Dropzone
//           onDrop={files => handleDrop(files, activeDevice)}
//           accept={{'image/*': []}}
//           maxFiles={1}>
//           {({getRootProps, getInputProps}) => (
//             <Paper
//               {...getRootProps()}
//               sx={{
//                 p: 2,
//                 border: '2px dashed',
//                 borderColor: error ? 'error.main' : 'grey.300',
//                 textAlign: 'center',
//                 cursor: 'pointer',
//                 '&:hover': {borderColor: 'primary.main'},
//                 bgcolor: error ? 'error.light' : 'transparent',
//               }}>
//               <input {...getInputProps()} />

//               {previews[activeDevice] ? (
//                 <Box>
//                   <Box sx={{position: 'relative', display: 'inline-block'}}>
//                     <img
//                       src={previews[activeDevice]}
//                       alt={`${activeDevice} preview`}
//                       style={{
//                         maxHeight: 150,
//                         maxWidth: '100%',
//                         borderRadius: 4,
//                       }}
//                     />

//                     <Chip
//                       label={
//                         activeDevice === 'mobile'
//                           ? '📱'
//                           : activeDevice === 'tablet'
//                           ? '💻'
//                           : '🖥️'
//                       }
//                       size="small"
//                       sx={{
//                         position: 'absolute',
//                         top: 5,
//                         left: 5,
//                         bgcolor: 'rgba(255,255,255,0.9)',
//                       }}
//                     />
//                   </Box>

//                   <MDTypography variant="caption" display="block" sx={{mt: 1}}>
//                     Click or drag to change {activeDevice} image
//                   </MDTypography>

//                   {localFiles[activeDevice] && (
//                     <MDTypography
//                       variant="caption"
//                       color="success"
//                       display="block">
//                       New: {localFiles[activeDevice].name}
//                     </MDTypography>
//                   )}
//                 </Box>
//               ) : (
//                 <Box>
//                   <Image sx={{fontSize: 40, color: 'text.secondary'}} />
//                   <MDTypography variant="body2" color="text">
//                     📱 {activeDevice === 'mobile' && 'Mobile (320px)'}
//                     {activeDevice === 'tablet' && 'Tablet (768px)'}
//                     {activeDevice === 'desktop' && 'Desktop (1200px)'}
//                   </MDTypography>
//                   <MDTypography variant="caption" color="textSecondary">
//                     Drag & drop or click to upload
//                   </MDTypography>
//                 </Box>
//               )}
//             </Paper>
//           )}
//         </Dropzone>

//         {previews[activeDevice] && (
//           <IconButton
//             size="small"
//             color="error"
//             onClick={() => clearImage(activeDevice)}
//             sx={{
//               position: 'absolute',
//               top: 5,
//               right: 5,
//               bgcolor: 'white',
//               '&:hover': {bgcolor: '#f5f5f5'},
//             }}>
//             <Delete fontSize="small" />
//           </IconButton>
//         )}
//       </Box>

//       <Box display="flex" gap={1} mt={1}>
//         {['mobile', 'tablet', 'desktop'].map(device => (
//           <Tooltip
//             key={device}
//             title={
//               previews[device] ? `${device} image set` : `No ${device} image`
//             }>
//             <Chip
//               icon={
//                 <span>
//                   {device === 'mobile'
//                     ? '📱'
//                     : device === 'tablet'
//                     ? '💻'
//                     : '🖥️'}
//                 </span>
//               }
//               label={device}
//               size="small"
//               color={previews[device] ? 'success' : 'default'}
//               variant={previews[device] ? 'filled' : 'outlined'}
//               onClick={() => setActiveDevice(device)}
//               sx={{cursor: 'pointer'}}
//             />
//           </Tooltip>
//         ))}
//       </Box>

//       {/* Copy from other device option */}
//       {/* {previews[activeDevice] === '' && hasAnyImage && (
//         <Box mt={1} display="flex" gap={1} justifyContent="center">
//           <MDTypography variant="caption" color="textSecondary">
//             Copy from:
//           </MDTypography>
//           {previews.mobile && activeDevice !== 'mobile' && (
//             <Button size="small" onClick={() => copyFromDevice('mobile')}>
//               📱 Mobile
//             </Button>
//           )}
//           {previews.tablet && activeDevice !== 'tablet' && (
//             <Button size="small" onClick={() => copyFromDevice('tablet')}>
//               💻 Tablet
//             </Button>
//           )}
//           {previews.desktop && activeDevice !== 'desktop' && (
//             <Button size="small" onClick={() => copyFromDevice('desktop')}>
//               🖥️ Desktop
//             </Button>
//           )}
//         </Box>
//       )} */}

//       {error && (
//         <MDTypography
//           variant="caption"
//           color="error"
//           sx={{mt: 0.5, display: 'block'}}>
//           {helperText}
//         </MDTypography>
//       )}
//     </Box>
//   );
// };

// export default DeviceImageUploader;

// OLD.....

// import {ContentCopy, Delete, Image} from '@mui/icons-material';
// import {Box, Button, Chip, IconButton, Paper, Tooltip} from '@mui/material';
// import MDTypography from 'components/MDTypography';
// import {useEffect, useState} from 'react';
// import DeviceTabs from './DeviceTabs';
// import Dropzone from 'react-dropzone';

// const DeviceImageUploader = ({
//   value = {},
//   onChange,
//   label,
//   files = {},
//   onFileChange,
//   error,
//   helperText,
//   deviceTypes = ['mobile', 'tablet', 'desktop'],
//   showCopyToAll = true,
// }) => {
//   const [activeDevice, setActiveDevice] = useState(
//     deviceTypes.includes('desktop') ? 'desktop' : deviceTypes[0] || 'desktop'
//   );

//   const [previews, setPreviews] = useState({});
//   const [localFiles, setLocalFiles] = useState({});

//   useEffect(() => {
//     const initialPreviews = {};
//     deviceTypes.forEach(device => {
//       if (value && value[device]) {
//         initialPreviews[device] = value[device];
//       }
//     });
//     setPreviews(initialPreviews);
//   }, [value, deviceTypes]);

//   useEffect(() => {
//     const initialFiles = {};
//     deviceTypes.forEach(device => {
//       if (files && files[device]) {
//         initialFiles[device] = files[device];
//       }
//     });
//     setLocalFiles(initialFiles);
//   }, [files, deviceTypes]);

//   const handleDrop = (acceptedFiles, device) => {
//     const newFile = acceptedFiles[0];
//     if (!newFile) return;

//     const objectUrl = URL.createObjectURL(newFile);

//     setPreviews(prev => ({
//       ...prev,
//       [device]: objectUrl,
//     }));

//     const updatedFiles = {
//       ...localFiles,
//       [device]: newFile,
//     };
//     setLocalFiles(updatedFiles);

//     const updatedValue = {
//       ...value,
//       [device]: objectUrl,
//     };
//     onChange(updatedValue);

//     if (onFileChange) {
//       onFileChange(device, newFile);
//     }
//   };

//   const clearImage = device => {
//     if (previews[device]?.startsWith('blob:')) {
//       URL.revokeObjectURL(previews[device]);
//     }

//     setPreviews(prev => {
//       const newPreviews = {...prev};
//       delete newPreviews[device];
//       return newPreviews;
//     });

//     setLocalFiles(prev => {
//       const newFiles = {...prev};
//       delete newFiles[device];
//       return newFiles;
//     });

//     const updatedValue = {...value};
//     delete updatedValue[device];

//     if (Object.keys(updatedValue).length === 0) {
//       onChange({});
//     } else {
//       onChange(updatedValue);
//     }

//     if (onFileChange) {
//       onFileChange(device, null);
//     }
//   };

//   const copyFromDevice = sourceDevice => {
//     if (!previews[sourceDevice]) return;

//     setPreviews(prev => ({
//       ...prev,
//       [activeDevice]: prev[sourceDevice],
//     }));

//     setLocalFiles(prev => ({
//       ...prev,
//       [activeDevice]: prev[sourceDevice],
//     }));

//     const updatedValue = {
//       ...value,
//       [activeDevice]: value[sourceDevice],
//     };
//     onChange(updatedValue);
//   };

//   const copyAllToDevices = () => {
//     if (!previews.desktop) return;

//     const updatedPreviews = {};
//     const updatedValue = {};

//     deviceTypes.forEach(device => {
//       updatedPreviews[device] = previews.desktop;
//       updatedValue[device] = value.desktop;
//     });

//     setPreviews(updatedPreviews);
//     setLocalFiles({
//       ...localFiles,
//       ...(deviceTypes.reduce((acc, device) => {
//         if (device !== 'desktop') {
//           acc[device] = localFiles.desktop;
//         }
//         return acc;
//       }, {}))
//     });

//     onChange(updatedValue);
//   };

//   const hasAnyImage = Object.values(previews).some(url => url);

//   const availableCopySources = deviceTypes.filter(
//     device => device !== activeDevice && previews[device]
//   );

//   return (
//     <Box>
//       <Box
//         display="flex"
//         justifyContent="space-between"
//         alignItems="center"
//         mb={1}>
//         <MDTypography variant="caption" color={error ? 'error' : 'text'}>
//           {label} {hasAnyImage ? '' : ''}
//         </MDTypography>
//         {/* {showCopyToAll && previews.desktop && deviceTypes.length > 1 && (
//           <Tooltip title="Use desktop image for all devices">
//             <Button
//               size="small"
//               startIcon={<ContentCopy />}
//               onClick={copyAllToDevices}>
//               Copy to All
//             </Button>
//           </Tooltip>
//         )} */}
//       </Box>

//       {deviceTypes.length > 1 ? (
//         <DeviceTabs
//           activeDevice={activeDevice}
//           onDeviceChange={setActiveDevice}
//           deviceTypes={deviceTypes}
//         />
//       ) : (
//         <Box sx={{mb: 1}}>
//           <MDTypography variant="caption" color="textSecondary">
//             {deviceTypes[0] === 'desktop' ? '🖥️ Desktop' :
//              deviceTypes[0] === 'mobile' ? '📱 Mobile' :
//              deviceTypes[0] === 'tablet' ? '💻 Tablet' : deviceTypes[0]}
//           </MDTypography>
//         </Box>
//       )}

//       <Box sx={{position: 'relative'}}>
//         <Dropzone
//           onDrop={files => handleDrop(files, activeDevice)}
//           accept={{'image/*': []}}
//           maxFiles={1}>
//           {({getRootProps, getInputProps}) => (
//             <Paper
//               {...getRootProps()}
//               sx={{
//                 p: 2,
//                 border: '2px dashed',
//                 borderColor: error ? 'error.main' : 'grey.300',
//                 textAlign: 'center',
//                 cursor: 'pointer',
//                 '&:hover': {borderColor: 'primary.main'},
//                 bgcolor: error ? 'error.light' : 'transparent',
//               }}>
//               <input {...getInputProps()} />

//               {previews[activeDevice] ? (
//                 <Box>
//                   <Box sx={{position: 'relative', display: 'inline-block'}}>
//                     <img
//                       src={previews[activeDevice]}
//                       alt={`${activeDevice} preview`}
//                       style={{
//                         maxHeight: 150,
//                         maxWidth: '100%',
//                         borderRadius: 4,
//                       }}
//                     />

//                     <Chip
//                       label={
//                         activeDevice === 'mobile'
//                           ? '📱'
//                           : activeDevice === 'tablet'
//                           ? '💻'
//                           : '🖥️'
//                       }
//                       size="small"
//                       sx={{
//                         position: 'absolute',
//                         top: 5,
//                         left: 5,
//                         bgcolor: 'rgba(255,255,255,0.9)',
//                       }}
//                     />
//                   </Box>

//                   <MDTypography variant="caption" display="block" sx={{mt: 1}}>
//                     Click or drag to change {activeDevice} image
//                   </MDTypography>

//                   {localFiles[activeDevice] && (
//                     <MDTypography
//                       variant="caption"
//                       color="success"
//                       display="block">
//                       New: {localFiles[activeDevice].name}
//                     </MDTypography>
//                   )}
//                 </Box>
//               ) : (
//                 <Box>
//                   <Image sx={{fontSize: 40, color: 'text.secondary'}} />
//                   <MDTypography variant="body2" color="text">
//                     {activeDevice === 'mobile' && '📱 Mobile (320px)'}
//                     {activeDevice === 'tablet' && '💻 Tablet (768px)'}
//                     {activeDevice === 'desktop' && '🖥️ Desktop (1200px)'}
//                   </MDTypography>
//                   <MDTypography variant="caption" color="textSecondary">
//                     Drag & drop or click to upload
//                   </MDTypography>
//                 </Box>
//               )}
//             </Paper>
//           )}
//         </Dropzone>

//         {previews[activeDevice] && (
//           <IconButton
//             size="small"
//             color="error"
//             onClick={() => clearImage(activeDevice)}
//             sx={{
//               position: 'absolute',
//               top: 5,
//               right: 5,
//               bgcolor: 'white',
//               '&:hover': {bgcolor: '#f5f5f5'},
//             }}>
//             <Delete fontSize="small" />
//           </IconButton>
//         )}
//       </Box>

//       <Box display="flex" gap={1} mt={1}>
//         {deviceTypes.map(device => (
//           <Tooltip
//             key={device}
//             title={
//               previews[device] ? `${device} image set` : `No ${device} image`
//             }>
//             <Chip
//               icon={
//                 <span>
//                   {device === 'mobile'
//                     ? '📱'
//                     : device === 'tablet'
//                     ? '💻'
//                     : '🖥️'}
//                 </span>
//               }
//               label={device}
//               size="small"
//               color={previews[device] ? 'success' : 'default'}
//               variant={previews[device] ? 'filled' : 'outlined'}
//               onClick={() => setActiveDevice(device)}
//               sx={{cursor: 'pointer'}}
//             />
//           </Tooltip>
//         ))}
//       </Box>

//       {/* {!previews[activeDevice] && availableCopySources.length > 0 && (
//         <Box mt={1} display="flex" gap={1} justifyContent="center">
//           <MDTypography variant="caption" color="textSecondary">
//             Copy from:
//           </MDTypography>
//           {availableCopySources.map(source => (
//             <Button
//               key={source}
//               size="small"
//               onClick={() => copyFromDevice(source)}
//             >
//               {source === 'mobile' ? '📱 Mobile' :
//                source === 'tablet' ? '💻 Tablet' : '🖥️ Desktop'}
//             </Button>
//           ))}
//         </Box>
//       )} */}

//       {error && (
//         <MDTypography
//           variant="caption"
//           color="error"
//           sx={{mt: 0.5, display: 'block'}}>
//           {helperText}
//         </MDTypography>
//       )}
//     </Box>
//   );
// };

// export default DeviceImageUploader;
